import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, App } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStore } from '../../store/store'
import { DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '.'
import { colId, rowId } from '../../constants/constants'
import { useTranslation } from 'react-i18next'

const dotStyle = {
  position: 'absolute',
  width: 5,
  height: 5,
  borderRadius: 20,
  backgroundColor: "#9e9e9e",
}

const AdderButton = ({
  isLeftButton,
  isFromHeader,
  isControlRowHovered,
  setParentLineHovered,
  columnLength,
  boxId,
  placementIndex,
  rowElementRef,
}) => {
  const updateBox = useStore(({ updateBox }) => updateBox)
  const { t } = useTranslation()
  const { modal: modalApi } = App.useApp()

  const [isHovered, setIsHovered] = useState(false)
  const showButton = isControlRowHovered || isHovered
  const basePosition = useMemo(() => {
    if (isLeftButton) return isFromHeader ? "left" : "top"
    return isFromHeader ? "right" : "bottom"
  }, [isLeftButton, isFromHeader])

  const secondBasePosition = useMemo(() => {
    return isFromHeader ? "top" : "left"
  }, [isFromHeader])

  const transform = useMemo(() => {
    if (isLeftButton) return `translate${isFromHeader ? "X" : "Y"}(-50%)`
    return `translate${isFromHeader ? "X" : "Y"}(50%)`
  }, [isLeftButton, isFromHeader])

  const handleOnMouseEnter = () => {
    setIsHovered(true);
    setParentLineHovered({ isHovered: true, isLeftButton })
  }

  const handleOnMouseLeave = () => {
    setIsHovered(false);
    setParentLineHovered({ isHovered: false })
  }

  const handleAddCol = () => {
    const colName =
      'Col ' + String.fromCharCode(65 + columnLength);

    updateBox(boxId, (box) => {
      if (box.content.columns.length !== 0) {
        const newColumnWidth = DEFAULT_COLUMN_WIDTH;
        const maxSubstractable = box.content.columns.reduce((acc, col) => acc + col.width - MIN_COLUMN_WIDTH, 0);
        const canAddColumn = maxSubstractable > newColumnWidth;
        if (canAddColumn) {
          const boxCurrentWidth = box.content.columns.reduce((acc, col) => acc + col.width, 0);
          box.content.columns.forEach((col) => {
            const colProportion = (col.width - MIN_COLUMN_WIDTH) / (boxCurrentWidth - (box.content.columns.length * MIN_COLUMN_WIDTH));
            const toRemove = newColumnWidth * colProportion;
            const newWidth = col.width - toRemove;
            if (newWidth >= MIN_COLUMN_WIDTH) {
              col.width = newWidth;
            } else {
              console.error('Not enough space to add column')
            }
          });
          const newColumnKey = colId();
          box.content.columns.splice(placementIndex, 0, {
            key: newColumnKey,
            title: colName,
            width: newColumnWidth,
          });

          // Mettre à jour columnsDisposition
          if (box.content.columnsDisposition && box.content.columnsDisposition.length > 0) {
            // Fonction récursive pour trouver la position d'insertion dans columnsDisposition
            const findInsertionPosition = (columns, targetIndex, currentIndex = { value: 0 }) => {
              for (let i = 0; i < columns.length; i++) {
                if (columns[i].children) {
                  // Colonne parente : parcourir récursivement les enfants
                  const result = findInsertionPosition(columns[i].children, targetIndex, currentIndex);
                  if (result !== null) {
                    if (result.index === 0) {
                      return { parent: null, index: i, isChild: false };
                    }
                    if (result.index === columns[i].children.length) {
                      return { parent: null, index: i + result.index, isChild: false };
                    }
                    return { parent: columns[i], index: result.index, isChild: true };
                  }
                } else {
                  // Colonne feuille
                  if (currentIndex.value === targetIndex) {
                    return { parent: null, index: i, isChild: false };
                  }
                  currentIndex.value++;
                }
              }
              // Si on arrive ici, on doit insérer à la fin
              if (currentIndex.value === targetIndex) {
                return { parent: null, index: columns.length, isChild: false };
              }
              return null;
            };

            const insertionPos = findInsertionPosition(box.content.columnsDisposition, placementIndex);

            if (insertionPos) {
              const newColumnDisposition = {
                key: newColumnKey,
                title: colName,
                width: newColumnWidth,
              };

              if (insertionPos.isChild && insertionPos.parent) {
                // Insérer dans les children d'un parent
                insertionPos.parent.children.splice(insertionPos.index, 0, newColumnDisposition);
              } else {
                // Insérer au niveau racine
                box.content.columnsDisposition.splice(insertionPos.index, 0, newColumnDisposition);
              }
            } else {
              // Si on ne trouve pas la position, ajouter à la fin au niveau racine
              box.content.columnsDisposition.push({
                key: newColumnKey,
                title: colName,
                width: newColumnWidth,
              });
            }
          } // For flat tables without hierarchical headers, keep columnsDisposition empty.
        } else {
          modalApi.confirm({
            title: t('table.cantAddColumn'),
            okText: 'Ok',
          });
        }
      }
    });
  };

  const handleAddRow = useCallback(() => {
    updateBox(boxId, (selectedBox) => {
      const nextRow = Object.fromEntries(selectedBox.content.columns.map((col) => [col.key, '']));
      nextRow.key = rowId();
      nextRow.hiddenColumnsKeys = [];
      // Ligne de l'utilisateur, pas de l'API : la fusion auto des lignes-titre
      // (computeLabelRowMerges) l'ignore, sinon elle naîtrait fusionnée.
      nextRow.manual = true;

      const rowBelow = placementIndex < selectedBox.content.data.length
        ? selectedBox.content.data[placementIndex]
        : null;

      if (rowBelow &&
        rowBelow.hiddenColumnsKeys && rowBelow.hiddenColumnsKeys.length > 0) {
        const belowKeys = [...rowBelow.hiddenColumnsKeys]

        nextRow.hiddenColumnsKeys = belowKeys;

        // Copier les valeurs des colonnes cachées
        belowKeys.forEach((columnKey) => {
          if (rowBelow[columnKey] !== undefined) {
            nextRow[columnKey] = rowBelow[columnKey];
          }
        });
      }

      selectedBox.content.data.splice(placementIndex, 0, nextRow);
    });
  }, [boxId, placementIndex, updateBox])

  const handleOnClick = () => {
    if (isFromHeader) {
      handleAddCol()
    } else {
      handleAddRow()
    }
  }

  useEffect(() => {
    if (!rowElementRef?.current || isFromHeader) return
    const tr = rowElementRef.current
    const handler = (e) => {
      const { fromTop } = e.detail
      const isMyButton = isLeftButton ? fromTop : !fromTop
      if (!isMyButton) return
      handleAddRow()
      setParentLineHovered({ isHovered: false })
      setParentLineHovered({ isHovered: true, isLeftButton })
    }
    tr.addEventListener('sp-row-border-click', handler)
    return () => tr.removeEventListener('sp-row-border-click', handler)
  }, [rowElementRef, isFromHeader, isLeftButton, handleAddRow, setParentLineHovered])

  return (
    <div>
      <div
        onMouseEnter={handleOnMouseEnter}
        style={{
          [`${basePosition}`]: 0,
          [`${secondBasePosition}`]: -5,
          transform,
          ...dotStyle,
        }} />
      <Button
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        onClick={handleOnClick}
        className={`${showButton ? 'adder-btn-visible' : 'adder-btn-hidden'} ${
          isFromHeader
            ? (isLeftButton ? 'adder-btn-col--left' : 'adder-btn-col--right')
            : (isLeftButton ? 'adder-btn-row--top' : 'adder-btn-row--bottom')
        }`}
        icon={<FontAwesomeIcon icon={faPlus}
          style={{
            transition: "color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
            color: isHovered ? "white" : "rgba(0, 0, 0, 0.88)",
          }} />}
        style={{
          position: "absolute",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          [`${basePosition}`]: 0,
          [`${secondBasePosition}`]: -15,
          transform,
          padding: 0,
          backgroundColor: isHovered ? "#388bff" : undefined,
          borderColor: isHovered ? "white" : undefined,
        }}
      />
    </div>
  )
}

export default AdderButton
